import React, { useEffect, useState } from 'react';

interface SafeColorInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * A safe color input component that uses native HTML elements to avoid
 * Mantine ColorInput ref callback issues with React 18+.
 *
 * Features:
 * - Visual color picker with native <input type="color">
 * - Text input for manual hex value entry
 * - Bidirectional sync between both inputs
 * - Hex color validation
 * - Accessible with proper labels and titles
 */
export const SafeColorInput: React.FC<SafeColorInputProps> = ({
  value,
  onChange,
  label,
  placeholder = '#000000',
  disabled = false,
  className,
  style,
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // Allow empty string or valid hex colors (with or without #)
    if (newValue === '' || /^#?[0-9A-Fa-f]{0,6}$/.test(newValue)) {
      // Ensure hex values start with #
      const formattedValue = newValue && !newValue.startsWith('#') ? `#${newValue}` : newValue;
      setLocalValue(formattedValue);

      // Only call onChange for complete hex colors or empty string
      if (formattedValue === '' || /^#[0-9A-Fa-f]{6}$/.test(formattedValue)) {
        onChange(formattedValue);
      }
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '120px',
    ...style,
  };

  const colorPickerStyle: React.CSSProperties = {
    width: '30px',
    height: '30px',
    borderRadius: '16px',
    border: '1px solid #ced4da',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: '0',
    backgroundColor: 'transparent',
    opacity: disabled ? 0.6 : 1,
  };

  const textInputStyle: React.CSSProperties = {
    flexGrow: 1,
    height: '36px',
    borderRadius: '16px',
    border: '1px solid #ced4da',
    padding: '0 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    backgroundColor: disabled ? '#f8f9fa' : 'white',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <div className={className} style={containerStyle}>
      <input
        type="color"
        value={localValue || '#000000'}
        onChange={handleColorPickerChange}
        disabled={disabled}
        style={colorPickerStyle}
        title={`Select color for ${label}`}
        aria-label={`Color picker for ${label}`}
      />
      <input
        type="text"
        value={localValue}
        onChange={handleTextInputChange}
        disabled={disabled}
        placeholder={placeholder}
        style={textInputStyle}
        title={`Hex color value for ${label}`}
        aria-label={`Hex color input for ${label}`}
        maxLength={7} // #FFFFFF
      />
    </div>
  );
};
